import { omit } from 'lodash-es';
import { getAllCountryCodesByRegions } from '@intelligence/v3/utils/region';
import { DataMethod } from '@intelligence/v3/enums/download';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import commonSetting from '@intelligence/components/DownloadCommon/DownloadPage/configs/commonSetting';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { shouldUseSSE } from '@intelligence/components/DownloadCommon/DownloadPage/utils/sse';

/**
 * 根据品类数据源和genre_dimension获取group_by配置
 * @param genreSource 品类数据源 ('sensortower' | 'appmagic')
 * @param genreDimension 用户选择的品类维度
 */
const getGenreGroupBy = (genreSource: string, genreDimension: string) => {
  if (genreSource === GENRE_SOURCE_MAP.SENSORTOWER) {
    // Sensor Tower 数据源
    switch (genreDimension) {
      case 'main_genre':
        return ['sensortower_genre'];
      case 'sub_genre':
        return ['sensortower_genre', 'sensortower_sub_genre'];
      default:
        return ['sensortower_genre', 'sensortower_sub_genre'];
    }
  } else {
    // App Magic 数据源
    switch (genreDimension) {
      case 'super_genre':
        return ['super_genre'];
      case 'major_category':
        return ['super_genre', 'major_category'];
      case 'mid_category':
        return ['super_genre', 'major_category', 'mid_category'];
      case 'sub_category':
        return ['super_genre', 'major_category', 'mid_category', 'sub_category'];
      default:
        return ['super_genre', 'major_category'];
    }
  }
};

/**
 * 根据品类数据源获取默认的group_by配置（当用户未选择genre_dimension时使用）
 */
const getDefaultGenreGroupBy = (genreSource: string) => {
  if (genreSource === GENRE_SOURCE_MAP.SENSORTOWER) {
    return ['sensortower_genre', 'sensortower_sub_genre']; // Sensor Tower 默认按 Sub Genre 分组
  }
  return ['super_genre', 'major_category']; // App Magic 默认按 Level-1 Genre 分组
};

const groupByGenerators = [
  {
    formModelKey: DOWNLOAD_CFG_KEY.DATE_METHOD,
    groupByKey: 'date',
    condition: (payload: string) => payload !== 'aggregated',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'region',
    condition: (payload: string) => payload === 'region',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'market',
    condition: (payload: string) => payload === 'market',
  },
];

const groupByFormModelKeys = groupByGenerators.map((generator) => generator?.formModelKey);

const getFileName = (params: any) => {
  const startDate = params?.start_date || '';
  const endDate = params?.end_date || '';
  const newStyleDownloadFileName = params?.new_style_donwload_file_name || '';
  if (!!startDate && !!endDate) {
    return `${newStyleDownloadFileName}_${startDate}_${endDate}`;
  }
  return `${newStyleDownloadFileName}`;
};

/**
 * 获取下载的markets入参
 * @param params 参数对象
 * @return 下载入参的国家列表
 */
const getDownloadMarkets = (params?: IRecord): string[] => {
  const markets = params?.markets || [];
  // @ts-ignore
  const dataMethod = params?.[DOWNLOAD_CFG_KEY.DATA_METHOD];
  if (dataMethod !== DataMethod.SelectTop) return markets;

  const marketsTmp =
    Array.isArray(markets) && markets.length > 0 ? markets : getAllCountryCodesByRegions(params?.regionOptions);
  return marketsTmp.slice(0, params?.[DOWNLOAD_CFG_KEY.SELECT_TOP]);
};

const getDownloadApiParams = <T extends IGenreMarketProfileFetchParams & IRecord = any>(params: T): T => {
  if (!params) return {} as unknown as T;

  // 获取品类数据源和用户选择的品类维度
  const genreSource = params?.genreSource || GENRE_SOURCE_MAP.SENSORTOWER;
  const genreDimension = params?.genre_dimension;

  // 根据品类数据源和品类维度获取基础group_by配置
  const genreGroupBy = genreDimension
    ? getGenreGroupBy(genreSource, genreDimension)
    : getDefaultGenreGroupBy(genreSource);

  const groupBys = (() => {
    const groupBys: string[] = [...genreGroupBy];

    // 处理 Time Settings 和 Region Settings
    groupByGenerators.forEach((generator) => {
      const formModelKey = generator?.formModelKey;
      const groupByKey = generator?.groupByKey;
      const condition = generator?.condition;
      if (!!condition?.(params?.[formModelKey])) {
        groupBys.push(groupByKey);
      }
    });

    return groupBys;
  })();

  const useMarketInfo = (() => {
    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'market') {
      return ['market -> region', 'market -> en', 'market -> cn', 'intel_region -> en', 'intel_region -> cn'];
    }

    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'region') {
      return ['intel_region -> en', 'intel_region -> cn'];
    }

    return [];
  })();

  const apiParams = {
    ...omit(params, [
      ...groupByFormModelKeys,
      DOWNLOAD_CFG_KEY.DATA_METHOD,
      DOWNLOAD_CFG_KEY.SELECT_TOP,
      'data_amount',
      'file_format',
      'file_name',
      'metric',
      'new_style_donwload_file_name',
      'ratio',
      'regionOptions',
      'genre_dimension', // 移除下载弹窗特有的字段
      'genreSource', // 移除品类数据源字段（仅供前端使用）
      'dimension', // 移除页面维度字段（仅供前端使用）
    ]),
    markets: getDownloadMarkets(params),
    group_by: groupBys,
    order_by: params?.order_by || [],
    // 保留搜索关键字参数
    keyword: params?.keyword || '',
    page_size: 10000,
    top: {
      group_by: genreGroupBy,
      order_by: params?.order_by || [],
      size: params?.select_top,
    },
    use_market_info: useMarketInfo,
    use_sse_page: {
      max_size: MAX_ROWS_LIMIT,
    },
  } as unknown as T;

  if (params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] === 'selectAll') {
    delete apiParams.top;
  }

  return apiParams;
};

export default (params: any) => {
  const apiParams = getDownloadApiParams(params);
  // 检查是否启用 SSE 模式
  const useSSE = shouldUseSSE(apiParams);

  return {
    ...commonSetting,
    downloadMode: 'commonConf',
    downloadConfigKey: 'casualGenreProfile',
    downloadFetchApi: '/api/v1/intelligence_pc/game/genre_profile/metrics',
    downloadApiParams: apiParams,
    isUsingNewMethod: false,
    // SSE 模式下不需要 fetchOnlyOnce，因为数据是流式传输的
    fetchOnlyOnce: !useSSE,
    dataKey: undefined,
    $$fileName: getFileName(params),
    regionOptions: params?.regionOptions,
  };
};
